import { useEffect, useRef, type MutableRefObject } from 'react';
import type { WithdrawElementOverlayHandle } from '@whop/elements/wallet';
import { useWallet } from '../whopElementsAdapter';
import type { WhopPayoutsModalSession } from '../contexts/WhopPayoutsModalContext';

export interface WhopWithdrawOverlayBridgeProps {
  open: boolean;
  /** Remount overlay when a fresh scoped token is minted. */
  accessToken: string;
  sessionRef: MutableRefObject<WhopPayoutsModalSession | null>;
  onClose: () => void;
}

/** Opens Whop's withdraw overlay from the mounted `<WhopWallet>` (one overlay per page). */
export function WhopWithdrawOverlayBridge({
  open,
  accessToken,
  sessionRef,
  onClose,
}: WhopWithdrawOverlayBridgeProps): null {
  const wallet = useWallet();
  const overlayRef = useRef<WithdrawElementOverlayHandle | null>(null);

  useEffect(() => {
    overlayRef.current?.close();
    overlayRef.current = null;
  }, [accessToken, wallet]);

  useEffect(() => {
    if (!wallet || !open) {
      overlayRef.current?.close();
      return undefined;
    }

    if (!overlayRef.current) {
      overlayRef.current = wallet.createOverlay('withdraw', {
        onWithdrawalRequested: () => {
          sessionRef.current?.onWithdrawalComplete();
        },
        onDone: () => {
          onClose();
        },
      });
    }

    overlayRef.current.open();

    return () => {
      overlayRef.current?.close();
    };
  }, [wallet, open, onClose, sessionRef, accessToken]);

  return null;
}
